import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import GuestTable from "@/app/admin/_common/guestlist/GuestTable";
import { Input } from "@/app/admin/_common/ui/input";
import CuoButton from "@/app/admin/_common/CuoButton";
import { cn } from "@/app/_utils/cn";
import { Show } from "@/typings";
import ShowService from "@/app/_services/ShowService";
import { debounce, filter } from "lodash";
import { Download, Search } from "lucide-react";

export default function ShowMasterGuests() {
  const { show_id } = useParams<{ show_id: string }>();
  const [showDetails, setShowDetails] = useState<Show>();
  const [isLoading, setIsLoading] = useState(true);
  const [guestList, setGuestList] = useState<Array<any>>([]);
  const [cachedGuestList, setCachedGuestList] = useState<Array<any>>([]);
  const [checkInCount, setCheckInCount] = useState(0);
  const [checkInTotal, setCheckInTotal] = useState(0);
  const [futureShows, setFutureShows] = useState<Array<any>>([]);
  const [compedOnly, setCompedOnly] = useState(false);

  async function getGuests() {
    if (!show_id) return;
    setIsLoading(true);
    try {
      const { guests, count, total } = await ShowService.getGuestList({ show_id, masterlist: true });
      setCheckInCount(count);
      setCheckInTotal(total);
      setGuestList(guests);
      setCachedGuestList(guests);
    } finally {
      setIsLoading(false);
    }
  }

  async function getShow() {
    if (!show_id) return;
    const show = await ShowService.getShow({ id: show_id });
    setShowDetails(show);
  }

  async function getFutureShows() {
    const res = await ShowService.getShow({ future: true });
    setFutureShows(Array.isArray(res) ? res : []);
  }

  useEffect(() => {
    getShow();
    getGuests();
    getFutureShows();
  }, [show_id]);

  const exportGuestList = async () => {
    if (!show_id) return;
    const res = await ShowService.exportGuestList(show_id, true);
    const url = window.URL.createObjectURL(res);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${showDetails?.title ?? "guestlist"}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleSearch = debounce((value: string) => {
    setGuestList(
      filter(cachedGuestList, (order) => {
        return (
          order?.guest_data?.first_name?.toLowerCase().includes(value.toLowerCase()) ||
          order?.guest_data?.last_name?.toLowerCase().includes(value.toLowerCase())
        );
      })
    );
  }, 300);

  function handleCompedOnly(next: boolean) {
    setCompedOnly(next);
    setGuestList(next ? filter(cachedGuestList, (order) => order?.comped === true) : cachedGuestList);
  }

  const checkInPct = checkInTotal ? Math.min(100, (checkInCount / checkInTotal) * 100) : 0;

  return (
    <div className="flex h-[calc(100%-10px)] flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-[220px]">
          <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
            <Search className="size-3.5 text-muted-foreground" />
          </div>
          <Input
            type="search"
            placeholder="Search guests…"
            onChange={(e) => handleSearch(e.target.value)}
            className="h-[30px] pl-9 text-[12.5px]"
          />
        </div>

        <div className="ml-auto flex items-center gap-3">
          <div className="w-[320px]">
            <div className="mb-1.5 flex items-center justify-between text-[12.5px]">
              <span className="text-muted-foreground">Checked in</span>
              <span className="text-foreground">{checkInCount} / {checkInTotal} guests</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-foreground" style={{ width: `${checkInPct}%` }} />
            </div>
          </div>

          <button
            type="button"
            onClick={() => handleCompedOnly(!compedOnly)}
            className={cn(
              "flex h-[30px] items-center justify-center rounded-md border px-3 text-[12.5px]",
              compedOnly ? "border-primary bg-primary/5 font-medium text-primary" : "border-input text-foreground hover:bg-muted"
            )}
          >
            Comps Only
          </button>

          <CuoButton variant="outline" icon={<Download className="size-3.5" />} label="Export" onClick={exportGuestList} />
        </div>
      </div>

      <GuestTable
        isLoading={isLoading}
        setCheckInCount={setCheckInCount}
        guests={guestList}
        containerClassName="flex min-h-0 flex-1 w-full flex-col overflow-hidden rounded-md border border-border bg-card"
        cachedGuests={cachedGuestList}
        setCachedGuests={setCachedGuestList}
        show={showDetails}
        showId={show_id}
        futureShows={futureShows}
        onChanged={getGuests}
      />
    </div>
  );
}
